import Form from '@components/Form'
import { VCard } from '@components/InfoCard'
import { TabPage } from '@components/TabPage'
import { zodResolver } from '@hookform/resolvers/zod'
import { updateMarketingApi } from 'api-validations/user-api'
import { useForm } from 'react-hook-form'
import { Link } from 'react-router'
import { useAccount } from 'shared-ui/active-operation'
import { usePanoramaMutation } from 'shared-ui/api-provider'
import { Loading, PanoramaButton } from 'shared-ui/components'
import { type LocalizationContext, useLocalization } from 'shared-ui/localization'
import { useAcceptEula, useUpdateDisplayName } from 'shared-ui/util-hooks'
import { isNonNil } from 'utils'
import { z } from 'zod'

import { path } from '../../../routes/paths'

const signUpFormSchema = (t: LocalizationContext['t'], email?: string) =>
  z
    .object({
      displayName: z.string().min(
        4,
        t(($) => $['your-name-must-contain-at-least-4-characters'])
      ),
      eulaAccept: z.boolean().refine((val) => val === true, {
        message: t(($) => $['agree-to-the-terms-and-conditions-to-continue'])
      }),
      marketingOptIn: z.boolean()
    })
    .refine((data) => data.displayName !== email, {
      path: ['displayName'],
      message: t(($) => $['your-name-cannot-be-the-same-as-your-email'])
    })

type SignUpFormData = z.infer<ReturnType<typeof signUpFormSchema>>

export const AcceptEulaPrompt: React.FC = () => {
  const account = useAccount()
  const { t } = useLocalization()

  const acceptEula = useAcceptEula()
  const updateDisplayName = useUpdateDisplayName()
  const updateMarketing = usePanoramaMutation(updateMarketingApi)

  const { register, handleSubmit, formState } = useForm<SignUpFormData>({
    resolver: zodResolver(signUpFormSchema(t, account.data?.email))
  })

  const onSubmit = handleSubmit((data) => {
    if (!account.isSuccess) return
    updateDisplayName.mutate({
      displayName: data.displayName,
      username: account.data.username,
      email: account.data.email
    })
    acceptEula.mutate({ username: account.data.username })
    const marketingOptIn = isNonNil(data.marketingOptIn)
    if (marketingOptIn) {
      updateMarketing.mutate({
        params: { username: account.data.username, op: marketingOptIn ? 'enable' : 'disable' }
      })
    }
  })

  if (account.isPending || acceptEula.isPending || acceptEula.isSuccess) return <Loading />

  // note: it seems like the user won't see server-side errors for updateDisplayName
  // if acceptEula succeeds.that may be the right way to do it, but it's worth noting.

  return (
    <TabPage title={t(($) => $['complete-sign-up'])}>
      <VCard.Wrapper>
        <VCard.Image src="" />
        <VCard.Content>
          <VCard.Title>{t(($) => $['lets-get-started'])}</VCard.Title>
          <VCard.Description>
            {updateDisplayName.isError && updateDisplayName.error.message}
            {acceptEula.isError && acceptEula.error.message}
          </VCard.Description>
          {updateDisplayName.isIdle && acceptEula.isIdle && (
            <div className="flex w-full">
              <Form onSubmit={onSubmit} className="">
                <Form.Label htmlFor="displayName" errorMsg={formState.errors.displayName?.message}>
                  {t(($) => $.labels.name)}
                </Form.Label>
                <Form.Input
                  type="text"
                  id="displayName"
                  autoComplete="off"
                  {...register('displayName')}
                  hasError={isNonNil(formState.errors.displayName)}
                />
                <div className="flex items-center gap-4 pl-2">
                  <Form.Input
                    type="checkbox"
                    id="eulaAccept"
                    className="mb-1 size-5 sm:size-4"
                    {...register('eulaAccept')}
                  />
                  <Form.Label htmlFor="eulaAccept">
                    {t(($) => $['i-have-read-and-agree-to-the'])}{' '}
                    <Link
                      to={path.publicEula}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-bold text-green-500 underline"
                    >
                      {t(($) => $['terms-and-conditions'])}
                    </Link>
                    .
                  </Form.Label>
                </div>
                <p className="mb-4 mt-0 pl-8 text-sm text-red-600" role="alert">
                  {formState.errors.eulaAccept?.message}
                </p>
                <div className="flex items-center gap-4 pl-2">
                  <Form.Input
                    type="checkbox"
                    id="marketingOptIn"
                    className="mb-1 size-5 sm:size-4"
                    {...register('marketingOptIn')}
                  />
                  <Form.Label htmlFor="marketingOptIn">
                    {t(($) => $.manageOperation.marketingEmailsDescription)}
                  </Form.Label>
                </div>
                <VCard.ButtonGroup>
                  <PanoramaButton className="max-w-48 justify-center">
                    {t(($) => $['create-account'])}
                  </PanoramaButton>
                </VCard.ButtonGroup>
              </Form>
            </div>
          )}
        </VCard.Content>
      </VCard.Wrapper>
    </TabPage>
  )
}
